// ============================================================
// Pont API de l'espace Déploiement (repris de recube-cicd).
// Attaque directement le backend Quarkus recube-cicd.
// Token propre `recube.token`, séparé du token Xbox du panel parent.
//
// URL à configurer via `VITE_DEPLOY_API_URL` (ex: https://cicd-api.recube.gg).
// En dev localhost sans var → fallback `http://localhost:8087` (Quarkus local).
// En prod sans var → API_BASE reste vide, les appels échouent avec un message
// explicite affiché par isDeployApiConfigured().
// ============================================================
const RAW_ENV = (import.meta.env.VITE_DEPLOY_API_URL as string | undefined) ?? '';

export const API_BASE = (() => {
  if (RAW_ENV) return RAW_ENV.replace(/\/+$/, '');
  // Fallback dev localhost uniquement — évite le piège "hostname:8087" en prod.
  if (typeof window !== 'undefined' && /^(localhost|127\.|::1)/.test(window.location.hostname)) {
    return 'http://localhost:8087';
  }
  return '';
})();

/** True si `VITE_DEPLOY_API_URL` est défini ou si on est en dev localhost. */
export function isDeployApiConfigured(): boolean {
  return API_BASE !== '';
}

const TOKEN_KEY = 'recube.token';

export const tokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export class ApiError extends Error {
  /** `body` : corps JSON de l'erreur quand il porte plus que `message` (ex. imageMismatch, citedBy). */
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiOptions extends RequestInit {
  json?: unknown;
}

export async function apiFetch<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  if (!isDeployApiConfigured()) {
    throw new ApiError(0,
      "URL de l'API recube-cicd non configurée. Renseigne VITE_DEPLOY_API_URL dans packages/web/.env puis relance le dev server.");
  }
  const { json, headers, ...rest } = opts;
  const token = tokenStorage.get();

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(headers as Record<string, string> | undefined ?? {}),
  };
  if (token) finalHeaders.Authorization = `Bearer ${token}`;

  let body = rest.body;
  if (json !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
    body = JSON.stringify(json);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...rest, body, headers: finalHeaders });

  if (res.status === 401) {
    tokenStorage.clear();
    window.dispatchEvent(new CustomEvent('recube:unauthenticated'));
    throw new ApiError(401, 'Session expirée');
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let body: unknown;
    try {
      const text = await res.text();
      if (text) {
        try { body = JSON.parse(text); msg = (body as { message?: string }).message ?? text; }
        catch { msg = text; }
      }
    } catch { /* ignore */ }
    throw new ApiError(res.status, msg, body);
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) return undefined as T;
  return (await res.json()) as T;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
